import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, X } from 'lucide-react';
import { nextMeterReading } from '../utils/meterReading';
import { recognizeMeterImage } from '../utils/meterImage';

const GUIDE_ASPECT_RATIO = 5.1;

const LiveMeterScanner = ({ label, onRead, onClose }) => {
  const videoRef = useRef(null);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const [status, setStatus] = useState('Opening camera...');
  const [error, setError] = useState('');
  const [aspectRatio, setAspectRatio] = useState(4 / 3);
  const readRef = useRef(onRead);
  const closeScannerRef = useRef(onClose);
  readRef.current = onRead;
  closeScannerRef.current = onClose;

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    const close = () => closeScannerRef.current();
    const onVisibility = () => { if (document.hidden) close(); };
    const onKeyDown = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
      if (event.key === 'Tab') {
        const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), select, input:not(:disabled)')];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pagehide', close);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pagehide', close);
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    let active = true;
    let stream;
    let worker;
    let digitWorker;
    let digitWorkerPromise;
    let timer;
    let reading = null;
    const video = videoRef.current;
    setError('');
    setStatus('Opening camera...');
    const release = () => {
      window.clearTimeout(timer);
      stream?.getTracks().forEach(track => track.stop());
      if (video.srcObject === stream) video.srcObject = null;
      stream = null;
      for (const reader of [worker, digitWorker]) {
        if (reader) Promise.resolve(reader.terminate()).catch(failure => console.error('Unable to stop meter reader:', failure));
      }
      worker = null;
      digitWorker = null;
    };
    const fail = failure => {
      if (!active) return;
      release();
      const messages = {
        NotAllowedError: 'Camera permission was denied. Allow camera access or enter the reading manually.',
        NotFoundError: 'No camera was found. Enter the reading manually.',
        NotReadableError: 'The camera is busy. Close other camera apps and try again.'
      };
      setError(messages[failure?.name] || failure?.message || 'Unable to scan this meter. Enter the reading manually.');
      setStatus('');
    };
    const start = async () => {
      try {
        if (window.isSecureContext === false) throw new Error('Live scanning requires HTTPS on your phone. Manual entry is still available.');
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('Live camera scanning is not supported in this browser. Enter the reading manually.');
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false });
        if (!active) { release(); return; }
        video.srcObject = stream;
        await video.play();
        if (!active) return;
        if (video.videoWidth && video.videoHeight) setAspectRatio(video.videoWidth / video.videoHeight);
        setStatus('Preparing number reader...');
        const { createWorker } = await import('tesseract.js');
        if (!active) return;
        worker = await createWorker('eng');
        if (!active) { release(); return; }
        await worker.setParameters({ tessedit_char_whitelist: '0123456789.', tessedit_pageseg_mode: '7', user_defined_dpi: '300' });
        if (!active) return;
        const getDigitWorker = () => {
          if (!digitWorkerPromise) {
            setStatus('Preparing mechanical digit reader...');
            digitWorkerPromise = (async () => {
              const reader = await createWorker('eng', 0, { cachePath: 'meter-digits-legacy-v1' });
              if (!active) { await reader.terminate(); return null; }
              digitWorker = reader;
              await reader.setParameters({
                tessedit_char_whitelist: '0123456789.', tessedit_pageseg_mode: '10', user_defined_dpi: '300',
                classify_enable_learning: '0', classify_enable_adaptive_matcher: '0'
              });
              return active ? reader : null;
            })();
          }
          return digitWorkerPromise;
        };
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) throw new Error('This browser cannot process camera frames. Enter the reading manually.');
        const scan = async () => {
          if (!active) return;
          try {
            if (video.readyState >= 2 && video.videoWidth && video.videoHeight) {
              const width = video.videoWidth * 0.8;
              const height = Math.min(video.videoHeight, width / GUIDE_ASPECT_RATIO);
              canvas.width = Math.max(1000, Math.min(1920, Math.round(width)));
              canvas.height = Math.max(1, Math.round(canvas.width * height / width));
              context.drawImage(video, video.videoWidth * 0.1, (video.videoHeight - height) / 2, width, height, 0, 0, canvas.width, canvas.height);
              const value = await recognizeMeterImage(worker, canvas,
                () => context.getImageData(0, 0, canvas.width, canvas.height),
                image => {
                  const wheelCanvas = document.createElement('canvas');
                  wheelCanvas.width = image.width;
                  wheelCanvas.height = image.height;
                  const wheelContext = wheelCanvas.getContext('2d');
                  if (!wheelContext) throw new Error('This browser cannot process counter wheels. Enter the reading manually.');
                  const pixels = wheelContext.createImageData(image.width, image.height);
                  pixels.data.set(image.data);
                  wheelContext.putImageData(pixels, 0, 0);
                  return wheelCanvas;
                }, () => active, getDigitWorker);
              if (!active) return;
              reading = nextMeterReading(reading, value);
              setStatus(reading.value ? `${reading.value} (${Math.min(reading.count, 3)}/3)` : 'Keep all four hour digits and the decimal wheels visible. If the needle covers a digit, move the phone sideways to reveal it. Move closer if the digits are too small, or enter the reading manually.');
              if (reading.count >= 3) {
                active = false;
                release();
                readRef.current(reading.value);
                return;
              }
            }
            if (active) timer = window.setTimeout(scan, 650);
          } catch (failure) { fail(failure); }
        };
        setStatus('Looking for a clear reading...');
        scan();
      } catch (failure) { fail(failure); }
    };
    start();
    return () => {
      active = false;
      release();
    };
  }, []);

  return createPortal(<div style={{ position: 'fixed', inset: 0, zIndex: 4000, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px', boxSizing: 'border-box' }}>
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-label={`Scan ${label}`} style={{ backgroundColor: 'white', width: '100%', maxWidth: '520px', maxHeight: 'calc(100dvh - 24px)', overflowY: 'auto', borderRadius: '8px' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', gap: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>Scan {label}</h3>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close meter scanner" title="Close meter scanner" style={{ width: '44px', height: '44px', flexShrink: 0, display: 'grid', placeItems: 'center', padding: 0, background: 'transparent', border: 0, cursor: 'pointer' }}><X size={22} /></button>
      </header>
      {!error && <>
      <div style={{ padding: '0 1rem 0.75rem' }}>
        <p style={{ margin: 0 }}>Keep the complete counter inside the box and the digits level. If the needle covers a digit, move the phone sideways to reveal it. Decimal places are automatic: five digits means one decimal, six means two. A printed decimal point is not required.</p>
      </div>
      <div style={{ position: 'relative', width: '100%', maxWidth: `${aspectRatio * 45}dvh`, margin: '0 auto', aspectRatio, background: '#111', overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted playsInline aria-label="Live meter camera" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }} />
        <div aria-hidden="true" data-testid="meter-scan-guide" style={{ position: 'absolute', left: '10%', top: '50%', transform: 'translateY(-50%)', width: '80%', aspectRatio: GUIDE_ASPECT_RATIO, border: '2px solid #43e0b5', boxSizing: 'border-box', boxShadow: '0 0 0 999px rgba(0,0,0,0.3)', pointerEvents: 'none' }} />
      </div></>}
      <div style={{ padding: '1rem', minHeight: '3rem' }}>
        {error ? <p role="alert" style={{ margin: 0, color: '#b02a37' }}>{error}</p> :
          <output aria-live="polite" style={{ fontSize: '18px', fontVariantNumeric: 'tabular-nums' }}>{status}</output>
        }
      </div>
    </section>
  </div>, document.body);
};

const MeterScanButton = ({ label, disabled = false, onRead }) => {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" aria-label={`Scan ${label}`} title={`Scan ${label}`} disabled={!!disabled} onClick={() => setOpen(true)} style={{ width: '44px', height: '44px', flexShrink: 0, display: 'grid', placeItems: 'center', padding: 0, border: '1px solid #adb5bd', borderRadius: '4px', backgroundColor: 'white', color: '#087f8c', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}><Camera size={21} /></button>
    {open && !disabled && <LiveMeterScanner label={label} onClose={() => setOpen(false)} onRead={value => { onRead(value); setOpen(false); }} />}
  </>;
};

export default MeterScanButton;